import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '../../components/ui/PageLayout';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';
import workflowService from '../../services/workflowService';

const WorkflowBuilder = () => {
  const { user } = useAuth();
  
  const [workflowNodes, setWorkflowNodes] = useState([]);
  const [workflowConnections, setWorkflowConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const nodeIdCounter = useRef(0);

  const nodeTypes = [
    { type: 'trigger', label: 'Form Submit', icon: 'Play', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
    { type: 'trigger', label: 'Email Opened', icon: 'Mail', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { type: 'trigger', label: 'Lead Created', icon: 'UserPlus', color: 'text-cyan-600', bgColor: 'bg-cyan-50', borderColor: 'border-cyan-200' },
    { type: 'action', label: 'Send Email', icon: 'Send', color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
    { type: 'action', label: 'Send SMS', icon: 'MessageSquare', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
    { type: 'action', label: 'Update Lead', icon: 'Edit', color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
    { type: 'condition', label: 'If/Then', icon: 'GitBranch', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
    { type: 'delay', label: 'Wait', icon: 'Clock', color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' }
  ];

  const addNode = (nodeType) => {
    const id = `node_${++nodeIdCounter.current}`;
    const newNode = {
      id,
      type: nodeType?.type,
      label: nodeType?.label,
      icon: nodeType?.icon,
      color: nodeType?.color,
      bgColor: nodeType?.bgColor,
      borderColor: nodeType?.borderColor,
      x: 100 + (workflowNodes?.length * 20) % 400,
      y: 100 + Math.floor(workflowNodes?.length / 5) * 120,
      config: {}
    };
    setWorkflowNodes(prev => [...prev, newNode]);
    setSelectedNode(newNode);
  };

  const removeNode = (nodeId) => {
    setWorkflowNodes(prev => prev?.filter(n => n?.id !== nodeId));
    setWorkflowConnections(prev => prev?.filter(c => c?.from !== nodeId && c?.to !== nodeId));
    if (selectedNode?.id === nodeId) setSelectedNode(null);
  };

  const handleSaveWorkflow = async () => {
    if (!user?.id) {
      setError('Please sign in to save workflows');
      return;
    }
    if (!workflowName?.trim()) {
      setError('Workflow name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const workflowData = {
        user_id: user?.id,
        name: workflowName,
        description: workflowDescription,
        status: isActive ? 'active' : 'paused',
        nodes: workflowNodes,
        connections: workflowConnections,
        trigger_type: workflowNodes?.find(n => n?.type === 'trigger')?.label || 'manual'
      };

      const result = await workflowService?.createWorkflow(workflowData);

      if (result?.error) {
        setError(result?.error);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const clearCanvas = () => {
    if (window.confirm('Clear all nodes? This cannot be undone.')) {
      setWorkflowNodes([]);
      setWorkflowConnections([]);
      setSelectedNode(null);
    }
  };

  return (
    <PageLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Workflow Builder</h1>
          <p className="text-muted-foreground">Build automated workflows with drag-and-drop simplicity</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {saved && (
            <span className="flex items-center text-green-600 text-sm font-medium">
              <Icon name="CheckCircle" size={16} className="mr-1" /> Saved!
            </span>
          )}
          <Button variant="outline" onClick={clearCanvas} iconName="Trash2" iconPosition="left">
            Clear
          </Button>
          <Button variant="default" onClick={handleSaveWorkflow} loading={saving} iconName="Save" iconPosition="left">
            Save Workflow
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Panel - Workflow Settings */}
        <div className="lg:col-span-1 space-y-6">
          {/* Workflow Info */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-4">Workflow Settings</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                <textarea
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e?.target?.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Active</span>
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActive ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          </div>

          {/* Node Types */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-4">Add Nodes</h3>
            <div className="space-y-2">
              {nodeTypes?.map((nodeType, idx) => (
                <button
                  key={idx}
                  onClick={() => addNode(nodeType)}
                  className={`w-full flex items-center space-x-3 p-2 rounded-lg border ${nodeType?.borderColor} ${nodeType?.bgColor} hover:opacity-80 transition-opacity text-left`}
                >
                  <Icon name={nodeType?.icon} size={16} className={nodeType?.color} />
                  <div>
                    <div className={`text-xs font-semibold ${nodeType?.color}`}>{nodeType?.type?.toUpperCase()}</div>
                    <div className="text-xs text-gray-700">{nodeType?.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Canvas */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-lg p-4 min-h-[500px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Workflow Canvas</h3>
              <span className="text-xs text-muted-foreground">{workflowNodes?.length} nodes</span>
            </div>

            {workflowNodes?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-80 text-center border-2 border-dashed border-border rounded-lg">
                <Icon name="Workflow" size={48} className="text-muted-foreground mb-4" />
                <p className="text-muted-foreground font-medium">Your workflow canvas is empty</p>
                <p className="text-sm text-muted-foreground mt-1">Add nodes from the left panel to build your workflow</p>
              </div>
            ) : (
              <div className="space-y-3">
                {workflowNodes?.map((node, idx) => (
                  <div
                    key={node?.id}
                    onClick={() => setSelectedNode(node)}
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNode?.id === node?.id
                        ? 'border-blue-500 bg-blue-50'
                        : `${node?.borderColor} ${node?.bgColor} hover:opacity-80`
                    }`}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm">
                      <Icon name={node?.icon} size={16} className={node?.color} />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-gray-500 uppercase">{node?.type}</div>
                      <div className="text-sm font-medium text-gray-900">{node?.label}</div>
                    </div>
                    {idx < workflowNodes?.length - 1 && (
                      <Icon name="ArrowDown" size={16} className="text-gray-400" />
                    )}
                    <button
                      onClick={(e) => { e?.stopPropagation(); removeNode(node?.id); }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Node Config */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-4">Node Configuration</h3>
            {selectedNode ? (
              <div className="space-y-3">
                <div className={`flex items-center space-x-2 p-3 rounded-lg ${selectedNode?.bgColor} border ${selectedNode?.borderColor}`}>
                  <Icon name={selectedNode?.icon} size={20} className={selectedNode?.color} />
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">{selectedNode?.type}</div>
                    <div className="text-sm font-medium text-gray-900">{selectedNode?.label}</div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Node Label</label>
                  <input
                    type="text"
                    value={selectedNode?.label}
                    onChange={(e) => {
                      const updated = { ...selectedNode, label: e?.target?.value };
                      setSelectedNode(updated);
                      setWorkflowNodes(prev => prev?.map(n => n?.id === updated?.id ? updated : n));
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                  />
                </div>
                <button
                  onClick={() => removeNode(selectedNode?.id)}
                  className="w-full px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors"
                >
                  Remove Node
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="MousePointer" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Click a node to configure it</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default WorkflowBuilder;